import { SetStateAction } from "react";
import { makeCygnusApiCall } from "../../../api/api";
import { Approver } from "../../../Reusables/components/ClientApproverSetting/ClientApproverSetting.utils";

/**
 * Fetches all users eligible for approval settings.
 *
 * @param setFetchUsersProgress - Callback to toggle loading spinner
 * @param setUsers - Callback to set the list of users
 */
export const fetchAllUsers = async (
  setUsers: { (val: SetStateAction<Approver[]>): void; (arg: Approver[]): void }
) => {

  const response = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: `/subscriptions/roles/users/`,
    show_message: true,
    customized_error_message: "Failed to load users",
    isblob: false,
    forMDM: false,
  });

  if (response.success) {
    setUsers(response.data ?? []);
  }

};

/**
 * Fetches existing approver configuration for a client or firm.
 *
 * @param processedTemplateId - ID of the EL
 * @param templateId - ID of the template
 * @param setFetchApproversProgress - Callback to toggle loading spinner
 * @param setApprovers - Callback to set list of approvers
 * @param setApprovalCheck - Callback to toggle approval required flag
 * @param setApprovalType - Callback to set approval workflow type
 */
export const fetchExistingApprovers = async (
  templateId: number | null,
  processedTemplateId: number | null,
  setInitialApprovers: { (val: SetStateAction<Approver[]>): void; (arg: Approver[]): void },
  setSelectedApprovers: { (val: SetStateAction<Approver[]>): void; (arg: Approver[]): void },
  setApprovalCheck: { (val: SetStateAction<boolean>): void; (arg: boolean): void },
  setApprovalType: { (val: SetStateAction<"Parallel" | "Sequential">): void; (arg: "Parallel" | "Sequential"): void },
  setFetchApproversProgress: { (val: SetStateAction<boolean>): void; (arg: boolean): void },
  clientMasterId?: number,
  reapplyRules?: boolean
) => {
  setFetchApproversProgress(true);

  const endpoint = processedTemplateId
    ? `/engagement-letter/el_approvals/?ProcessedTemplateId=${processedTemplateId}&ClientMasterId=${clientMasterId}&ReapplyRules=${reapplyRules}`
    : `/engagement-letter/template_approval_configuration_details/?TemplateId=${templateId}`;

  const response = await makeCygnusApiCall({
    type: "GET",
    payload: {},
    url: endpoint,
    show_message: true,
    customized_error_message: "Failed to load existing approvers",
    isblob: false,
    forMDM: true,
  });

  if (response.success && response.data?.length > 0) {
    const data = response.data;
    setInitialApprovers(data);
    setSelectedApprovers(data);
    setApprovalCheck(data[0].ApprovalRequired === "N");
    setApprovalType(data[0].ApprovalType ?? "Parallel");
  } else {
    setInitialApprovers([]);
    setSelectedApprovers([]);
  }

  setFetchApproversProgress(false);
};

/**
 * Saves updated approver settings for a client or firm.
 *
 * @param processedTemplateId - ID of the EL
 * @param templateId - ID of the template
 * @param approvalCheck - Whether approval is required or not
 * @param selectedApprovers - List of selected approvers
 * @param approvalRadio - The type of approval workflow ("Parallel" or "Sequential")
 * @param setSaveApproversProgress - Callback to toggle loading spinner
 * @param onSuccess - Callback to run after successful save
 */
export const saveApproverSettings = async (
  templateId: number | null,
  processedTemplateId: number | null,
  approvalCheck: boolean,
  selectedApprovers: Approver[],
  approvalRadio: "Parallel" | "Sequential",
  setSaveApproversProgress: { (val: SetStateAction<boolean>): void; (arg: boolean): void },
  onSuccess: () => void
) => {
  setSaveApproversProgress(true);

  const approverPayload = approvalCheck && !processedTemplateId
    ? [
        {
          TemplateId: templateId,
          ProcessedTemplateId: processedTemplateId,
          ApprovalRequired: "N",
          FirmDetailId: 0,
          ApprovalType: approvalRadio,
        },
      ]
    : selectedApprovers.map((a) => ({
        ...a,
        ApprovalRequired: "Y",
        ApprovalType: approvalRadio,
        TemplateId: templateId,
        ProcessedTemplateId: processedTemplateId,
      }));

  console.log(approverPayload)
  const endpoint = processedTemplateId
    ? `/engagement-letter/el_approvals/`
    : `/engagement-letter/template_approval_configuration_details/`;

  const response = await makeCygnusApiCall({
    type: "POST",
    url: endpoint,
    payload: approverPayload,
    show_message: true,
    customized_success_message: "Approval settings saved successfully",
    customized_failure_message: "Failed to save approvers",
    customized_error_message: "Failed to save approvers",
    isblob: false,
    forMDM: true,
  });

  setSaveApproversProgress(false);
  if (response.success) {
    onSuccess();
  }
};
